import { BookingState, BookingTransitionActor } from './booking.enums';

export type BookingTransitionKey = `${BookingState}->${BookingState}`;

export const BOOKING_TERMINAL_STATES: readonly BookingState[] = [
  BookingState.REJECTED,
  BookingState.CANCELLED,
  BookingState.CLOSED,
];

const DISPUTE_EXCLUDED_FROM: readonly BookingState[] = [
  ...BOOKING_TERMINAL_STATES,
  BookingState.IN_DISPUTE,
];

/** Valid target states per source (excludes dispute shortcut — see `getAllowedBookingTargetStates`). */
export const VALID_BOOKING_TRANSITIONS: Record<BookingState, readonly BookingState[]> = {
  [BookingState.DRAFT]: [BookingState.AWAITING_VENDOR_RESPONSE, BookingState.CANCELLED],
  [BookingState.REQUESTED]: [BookingState.AWAITING_VENDOR_RESPONSE, BookingState.CANCELLED],
  [BookingState.AWAITING_VENDOR_RESPONSE]: [
    BookingState.IN_NEGOTIATION,
    BookingState.ACCEPTED,
    BookingState.REJECTED,
    BookingState.CANCELLED,
  ],
  [BookingState.IN_NEGOTIATION]: [
    BookingState.ACCEPTED,
    BookingState.REJECTED,
    BookingState.CANCELLED,
  ],
  [BookingState.ACCEPTED]: [BookingState.AWAITING_ESCROW_FUNDING, BookingState.CANCELLED],
  [BookingState.AWAITING_ESCROW_FUNDING]: [BookingState.CONFIRMED, BookingState.CANCELLED],
  [BookingState.CONFIRMED]: [
    BookingState.IN_PROGRESS,
    BookingState.AWAITING_VENDOR_RECONFIRM,
    BookingState.CANCELLED,
  ],
  [BookingState.AWAITING_VENDOR_RECONFIRM]: [BookingState.CONFIRMED, BookingState.CANCELLED],
  [BookingState.IN_PROGRESS]: [BookingState.SERVICE_COMPLETED, BookingState.CANCELLED],
  [BookingState.SERVICE_COMPLETED]: [BookingState.CLOSED],
  [BookingState.IN_DISPUTE]: [BookingState.CLOSED],
  [BookingState.REJECTED]: [],
  [BookingState.CANCELLED]: [],
  [BookingState.CLOSED]: [],
};

/** RBAC: which actor may perform each transition (dispute edges merged at runtime). */
export const BOOKING_TRANSITION_ACTORS: Partial<
  Record<BookingTransitionKey, readonly BookingTransitionActor[]>
> = {
  [`${BookingState.DRAFT}->${BookingState.AWAITING_VENDOR_RESPONSE}`]: [
    BookingTransitionActor.HOST,
    BookingTransitionActor.SYSTEM,
  ],
  [`${BookingState.DRAFT}->${BookingState.CANCELLED}`]: [BookingTransitionActor.HOST],
  [`${BookingState.REQUESTED}->${BookingState.AWAITING_VENDOR_RESPONSE}`]: [
    BookingTransitionActor.HOST,
    BookingTransitionActor.SYSTEM,
  ],
  [`${BookingState.REQUESTED}->${BookingState.CANCELLED}`]: [BookingTransitionActor.HOST],
  [`${BookingState.AWAITING_VENDOR_RESPONSE}->${BookingState.IN_NEGOTIATION}`]: [
    BookingTransitionActor.VENDOR,
  ],
  [`${BookingState.AWAITING_VENDOR_RESPONSE}->${BookingState.ACCEPTED}`]: [
    BookingTransitionActor.VENDOR,
  ],
  [`${BookingState.AWAITING_VENDOR_RESPONSE}->${BookingState.REJECTED}`]: [
    BookingTransitionActor.VENDOR,
  ],
  [`${BookingState.AWAITING_VENDOR_RESPONSE}->${BookingState.CANCELLED}`]: [
    BookingTransitionActor.HOST,
    BookingTransitionActor.VENDOR,
  ],
  [`${BookingState.IN_NEGOTIATION}->${BookingState.ACCEPTED}`]: [
    BookingTransitionActor.HOST,
    BookingTransitionActor.VENDOR,
  ],
  [`${BookingState.IN_NEGOTIATION}->${BookingState.REJECTED}`]: [
    BookingTransitionActor.HOST,
    BookingTransitionActor.VENDOR,
  ],
  [`${BookingState.IN_NEGOTIATION}->${BookingState.CANCELLED}`]: [
    BookingTransitionActor.HOST,
    BookingTransitionActor.VENDOR,
  ],
  [`${BookingState.ACCEPTED}->${BookingState.AWAITING_ESCROW_FUNDING}`]: [
    BookingTransitionActor.HOST,
    BookingTransitionActor.SYSTEM,
  ],
  [`${BookingState.ACCEPTED}->${BookingState.CANCELLED}`]: [
    BookingTransitionActor.HOST,
    BookingTransitionActor.VENDOR,
  ],
  [`${BookingState.AWAITING_ESCROW_FUNDING}->${BookingState.CONFIRMED}`]: [
    BookingTransitionActor.SYSTEM,
    BookingTransitionActor.ADMIN,
  ],
  [`${BookingState.AWAITING_ESCROW_FUNDING}->${BookingState.CANCELLED}`]: [
    BookingTransitionActor.HOST,
    BookingTransitionActor.VENDOR,
    BookingTransitionActor.ADMIN,
  ],
  [`${BookingState.CONFIRMED}->${BookingState.IN_PROGRESS}`]: [
    BookingTransitionActor.HOST,
    BookingTransitionActor.VENDOR,
    BookingTransitionActor.ADMIN,
  ],
  [`${BookingState.CONFIRMED}->${BookingState.AWAITING_VENDOR_RECONFIRM}`]: [
    BookingTransitionActor.HOST,
    BookingTransitionActor.SYSTEM,
    BookingTransitionActor.ADMIN,
  ],
  [`${BookingState.CONFIRMED}->${BookingState.CANCELLED}`]: [
    BookingTransitionActor.HOST,
    BookingTransitionActor.VENDOR,
    BookingTransitionActor.ADMIN,
  ],
  [`${BookingState.AWAITING_VENDOR_RECONFIRM}->${BookingState.CONFIRMED}`]: [
    BookingTransitionActor.VENDOR,
    BookingTransitionActor.ADMIN,
  ],
  [`${BookingState.AWAITING_VENDOR_RECONFIRM}->${BookingState.CANCELLED}`]: [
    BookingTransitionActor.HOST,
    BookingTransitionActor.VENDOR,
    BookingTransitionActor.ADMIN,
  ],
  [`${BookingState.IN_PROGRESS}->${BookingState.SERVICE_COMPLETED}`]: [
    BookingTransitionActor.VENDOR,
    BookingTransitionActor.SYSTEM,
    BookingTransitionActor.ADMIN,
  ],
  [`${BookingState.IN_PROGRESS}->${BookingState.CANCELLED}`]: [
    BookingTransitionActor.HOST,
    BookingTransitionActor.ADMIN,
  ],
  [`${BookingState.SERVICE_COMPLETED}->${BookingState.CLOSED}`]: [
    BookingTransitionActor.HOST,
    BookingTransitionActor.ADMIN,
    BookingTransitionActor.SYSTEM,
  ],
  [`${BookingState.IN_DISPUTE}->${BookingState.CLOSED}`]: [
    BookingTransitionActor.ADMIN,
    BookingTransitionActor.HOST,
  ],
};

/** Target states that require a `reason` on the transition request. */
export const BOOKING_TRANSITION_TARGETS_REQUIRING_REASON: readonly BookingState[] = [
  BookingState.REJECTED,
  BookingState.CANCELLED,
  BookingState.IN_DISPUTE,
];

export function bookingTransitionKey(from: BookingState, to: BookingState): BookingTransitionKey {
  return `${from}->${to}`;
}

export function getAllowedBookingTargetStates(from: BookingState): readonly BookingState[] {
  const base = VALID_BOOKING_TRANSITIONS[from] ?? [];
  if (!DISPUTE_EXCLUDED_FROM.includes(from)) {
    return [...base, BookingState.IN_DISPUTE];
  }
  return base;
}

export function isValidBookingTransition(from: BookingState, to: BookingState): boolean {
  return getAllowedBookingTargetStates(from).includes(to);
}

export function getBookingTransitionActors(
  from: BookingState,
  to: BookingState,
): readonly BookingTransitionActor[] {
  if (to === BookingState.IN_DISPUTE && !DISPUTE_EXCLUDED_FROM.includes(from)) {
    return [BookingTransitionActor.HOST, BookingTransitionActor.VENDOR];
  }
  return BOOKING_TRANSITION_ACTORS[bookingTransitionKey(from, to)] ?? [];
}

export function canActorPerformBookingTransition(
  from: BookingState,
  to: BookingState,
  actor: BookingTransitionActor,
): boolean {
  return getBookingTransitionActors(from, to).includes(actor);
}

export function bookingTransitionRequiresReason(_from: BookingState, to: BookingState): boolean {
  return BOOKING_TRANSITION_TARGETS_REQUIRING_REASON.includes(to);
}
